import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, UserPlus, Loader2, Users, Mail, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-colors";
const empty = { resident_first_name: "", family_name: "", email: "" };

const EspaceSoignant = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [families, setFamilies] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portal/families").then((r) => setFamilies(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/portal/me")
      .then((r) => {
        if (r.data.space !== "soignant") { nav("/"); return; }
        setMe(r.data); load();
      })
      .catch(() => nav("/"));
  }, []); // eslint-disable-line

  const logout = async () => { await api.post("/portal/logout"); nav("/"); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/portal/families", form);
      toast.success(data.email_sent ? "Compte créé — email envoyé à la famille" : "Compte créé (email non envoyé)");
      setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen pt-32 pb-20 bg-accent/20" data-testid="espace-soignant-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Espace Soignant</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-light">Bonjour, {me.name}</h1>
          </div>
          <button onClick={logout} data-testid="soignant-logout-btn" className="flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm hover:bg-accent transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></span>
            <h2 className="font-serif-display text-2xl">Gestion des familles</h2>
          </div>

          <form onSubmit={submit} className="bg-card rounded-3xl border border-border/50 p-7 grid sm:grid-cols-3 gap-4" data-testid="create-family-form">
            <label className="block sm:col-span-1">
              <span className="text-sm text-foreground/70 mb-1.5 block">Prénom du résident</span>
              <input required className={inputCls} data-testid="family-resident" value={form.resident_first_name} onChange={(e) => setForm({ ...form, resident_first_name: e.target.value })} />
            </label>
            <label className="block sm:col-span-1">
              <span className="text-sm text-foreground/70 mb-1.5 block">Nom de la famille</span>
              <input required className={inputCls} data-testid="family-name" value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} />
            </label>
            <label className="block sm:col-span-1">
              <span className="text-sm text-foreground/70 mb-1.5 block">Adresse e-mail</span>
              <input required type="email" className={inputCls} data-testid="family-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <button disabled={busy} data-testid="create-family-btn" className="sm:col-span-3 rounded-full bg-primary text-primary-foreground py-3.5 flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform duration-300 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Créer le compte & envoyer les identifiants
            </button>
          </form>

          <div className="mt-8 space-y-3">
            <p className="text-sm text-muted-foreground">{families.length} famille(s) enregistrée(s)</p>
            {families.map((f) => (
              <div key={f.email} data-testid={`family-row-${f.email}`} className="bg-card rounded-2xl border border-border/50 p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-medium">Famille {f.name} <span className="text-muted-foreground font-normal">— résident : {f.resident_first_name}</span></p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1"><Mail className="w-3.5 h-3.5" /> {f.email}</p>
                </div>
                <span className="text-xs text-secondary-foreground bg-secondary/25 rounded-full px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" /> Accès envoyé</span>
              </div>
            ))}
            {families.length === 0 && <p className="text-muted-foreground text-sm">Aucune famille créée pour le moment.</p>}
          </div>
        </section>
      </div>
    </div>
  );
};

export default EspaceSoignant;
